import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { ROLE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import { ShieldCheck, LayoutDashboard, UserCircle2, Wrench, Crown, Users } from "lucide-react";

const ROLE_ICONS = {
  pmo: ShieldCheck, executive: Crown, it_admin: Users, it_executive: LayoutDashboard, engineer: Wrench,
};

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Carle2026!");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => { if (user) nav("/"); }, [user]);
  useEffect(() => { api.get("/auth/demo-accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);

  const doLogin = async (em) => {
    setErr(""); setLoading(true);
    try {
      await login(em || email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (e) { setErr(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#1F2733] text-white p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#BA0C2F]/25" />
        <div className="absolute -left-16 bottom-10 h-72 w-72 rounded-full bg-white/5" />
        <div className="flex items-center gap-3 relative">
          <div className="bg-white rounded-2xl px-3 py-2"><img src="https://customer-assets-jai6qajn.emergentagent.net/job_transform-dashboard-2/artifacts/5gfc3ket_download.jpg" alt="Carle Health" className="h-6 w-auto" /></div>
          <div className="font-extrabold text-lg">IT Digital Transformation</div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">Digital Transformation Solution</h1>
          <p className="mt-5 text-white/80 text-lg max-w-md leading-relaxed">
            The single system of record for the IT PMO — carrying the program from assessment through mitigation,
            planning, and executive reporting.
          </p>
          <div className="mt-8 flex gap-3 text-sm">
            {["Assess", "Plan", "Mitigate", "Stabilize"].map((p, i) => (
              <span key={p} className="px-3.5 py-1.5 rounded-full bg-white/15 font-semibold">{i + 1}. {p}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/60">Enterprise governance · HIPAA-aware · Role-based access</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <img src="https://customer-assets-jai6qajn.emergentagent.net/job_transform-dashboard-2/artifacts/5gfc3ket_download.jpg" alt="Carle Health" className="h-9 w-auto mb-6" />
          <h2 className="text-2xl font-extrabold text-[#1F2733]">Sign in</h2>
          <p className="text-sm text-[#5A687C] mt-1">Access your role-based workspace.</p>

          <div className="mt-6 space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#5A687C]">Email</label>
              <input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm focus:border-[#BA0C2F]" placeholder="you@carlehealth.org" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5A687C]">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm focus:border-[#BA0C2F]" placeholder="••••••••" />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-[#BA0C2F] bg-[#FDF2F4] border border-[#F8C4CC] rounded-xl px-3 py-2">{err}</div>}
            <button data-testid="login-submit-btn" disabled={loading} onClick={() => doLogin()}
              className="w-full bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full py-2.5 font-semibold shadow-sm transition-all disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-3 text-xs font-semibold text-[#98A2AF] uppercase tracking-wider">
              <div className="h-px flex-1 bg-[#DCE2EA]" /> Quick login (demo) <div className="h-px flex-1 bg-[#DCE2EA]" />
            </div>
            <div className="mt-4 grid gap-2">
              {accounts.map((a) => {
                const Icon = ROLE_ICONS[a.role] || UserCircle2;
                return (
                  <button key={a.email} data-testid={`quick-login-${a.role}`} onClick={() => { setEmail(a.email); doLogin(a.email); }}
                    className="flex items-center gap-3 p-2.5 rounded-2xl border border-[#DCE2EA] hover:border-[#BA0C2F] hover:bg-[#FBEAED] transition-all text-left">
                    <div className="h-9 w-9 rounded-full bg-[#FBEAED] text-[#BA0C2F] flex items-center justify-center shrink-0"><Icon className="h-4.5 w-4.5" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1F2733] truncate">{a.name} · <span className="text-[#BA0C2F]">{ROLE_LABELS[a.role]}</span></div>
                      <div className="text-[11px] text-[#5A687C] truncate">{a.title || a.email}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
